import type {
  DatafeedErrorCallback,
  HistoryCallback,
  IBasicDataFeed,
  LibrarySymbolInfo,
  OnReadyCallback,
  PeriodParams,
  ResolutionString,
  ResolveCallback,
  SearchSymbolsCallback,
  SubscribeBarsCallback,
  SymbolResolveExtension,
} from '../../lib/charting_library';
import type { ChartLoadRecorder } from './chart-load-recorder';

export class ObservedDatafeed implements IBasicDataFeed {
  readonly #delegate: IBasicDataFeed;
  readonly #recorder: ChartLoadRecorder;

  constructor(delegate: IBasicDataFeed, recorder: ChartLoadRecorder) {
    this.#delegate = delegate;
    this.#recorder = recorder;
  }

  onReady(callback: OnReadyCallback) {
    this.#delegate.onReady(callback);
  }

  searchSymbols(userInput: string, exchange: string, symbolType: string, onResult: SearchSymbolsCallback) {
    this.#delegate.searchSymbols(userInput, exchange, symbolType, onResult);
  }

  resolveSymbol(
    symbolName: string,
    onResolve: ResolveCallback,
    onError: DatafeedErrorCallback,
    extension?: SymbolResolveExtension,
  ) {
    this.#recorder.updateMetadata({ symbol: symbolName });
    this.#recorder.mark('symbol_resolve_started');
    this.#delegate.resolveSymbol(
      symbolName,
      (symbolInfo) => {
        this.#recorder.mark('symbol_resolved');
        onResolve(symbolInfo);
      },
      (reason) => {
        this.#recorder.fail(`symbol_not_resolved:${reason}`);
        onError(reason);
      },
      extension,
    );
  }

  getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback,
  ) {
    const isFirstDataRequest = periodParams.firstDataRequest;
    if (isFirstDataRequest) {
      this.#recorder.updateMetadata({ symbol: symbolInfo.name, resolution });
      this.#recorder.mark('historical_request_started');
    }

    this.#delegate.getBars(
      symbolInfo,
      resolution,
      periodParams,
      (bars, metadata) => {
        if (isFirstDataRequest) this.#recorder.mark('historical_data_received');
        onResult(bars, metadata);
      },
      (reason) => {
        if (isFirstDataRequest) this.#recorder.fail(`historical_data_failed:${reason}`);
        onError(reason);
      },
    );
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    onResetCacheNeededCallback: () => void,
  ) {
    this.#delegate.subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeededCallback);
  }

  unsubscribeBars(listenerGuid: string) {
    this.#delegate.unsubscribeBars(listenerGuid);
  }
}
